import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Heart, Users, BookOpen, Utensils, Shirt, 
  GraduationCap, MessageCircle, Mail,
  ArrowRight, Calendar, MapPin, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SectionHeading from '../components/SectionHeading';
import ProgramCard from '../components/ProgramCard';
import ImpactCard from '../components/ImpactCard';
import LaserBorder from '../components/LaserBorder';
import PrayerTimes from '../components/PrayerTimes';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const programs = [
  {
    icon: Heart,
    title: 'Orphan Support',
    description: 'Comprehensive care for orphans including food, clothing, education, and shelter.',
    span: 'col-span-12 md:col-span-6 lg:col-span-8',
  },
  {
    icon: Utensils,
    title: 'Food Bank',
    description: 'Food distribution to families in need.',
    span: 'col-span-12 md:col-span-6 lg:col-span-4',
  },
  {
    icon: Shirt,
    title: 'Clothing & Winter Kits',
    description: 'Essential clothing and winter supplies for vulnerable families.',
    span: 'col-span-12 md:col-span-6',
  },
  {
    icon: GraduationCap,
    title: 'Education Support',
    description: 'Tutoring, school supplies, and Islamic education.',
    span: 'col-span-12 md:col-span-6',
  },
];

const HERO_IMAGES = [
  { src: '/hero-prayer.jpg',     alt: 'True North Muslim Community gathered for prayer' },
  { src: '/hero-volunteers.jpg', alt: 'TNMC volunteers — Feed Hope, Share Kindness' },
];

const HomePage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const [homepage, setHomepage] = useState({
    hero_badge: 'Strengthening Faith. Serving Community. Supporting Orphans.',
    hero_title: 'True North Muslim Community',
    mission_text: 'True North Muslim Community is dedicated to supporting orphans, vulnerable families, and building a stronger Muslim community in Canada.',
  });

  useEffect(() => {
    axios.get(`${API}/content/homepage`)
      .then((res) => {
        if (res.data && res.data.hero_title) {
          setHomepage((prev) => ({ ...prev, ...res.data }));
        }
      })
      .catch(() => {/* fall back to defaults */});
  }, []);

  // Rotate hero image every 7 seconds
  useEffect(() => {
    const t = setInterval(() => {
      setHeroIndex((i) => (i + 1) % HERO_IMAGES.length);
    }, 7000);
    return () => clearInterval(t);
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast.success('Thank you for subscribing!');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('You are already subscribed!');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Slideshow: cross-fading community photos with Ken Burns zoom */}
        <div className="absolute inset-0" data-testid="hero-slideshow">
          {HERO_IMAGES.map((img, i) => (
            <motion.div
              key={img.src}
              className="absolute inset-0"
              initial={false}
              animate={{ opacity: heroIndex === i ? 1 : 0, scale: heroIndex === i ? 1.05 : 1 }}
              transition={{ opacity: { duration: 1.5, ease: 'easeInOut' }, scale: { duration: 8, ease: 'linear' } }}
              data-testid={`hero-slide-${i}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover"
                style={{ filter: 'brightness(0.65) saturate(1.1)' }}
              />
            </motion.div>
          ))}
        </div>

        {/* Deep navy gradient overlay — brand colors, keeps text legible */}
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/70 to-background/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-transparent to-background" />

        {/* Subtle Islamic geometric pattern on top */}
        <div className="absolute inset-0 islamic-pattern opacity-30" />

        {/* Neon teal glow orbs for depth */}
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-24 right-1/4 w-96 h-96 rounded-full bg-secondary/10 blur-3xl" />

        {/* Canadian Flag - Top Right */}
        <div className="absolute top-24 right-4 sm:right-8 lg:right-12 z-10">
          <img
            src="https://flagcdn.com/w320/ca.png"
            alt="Canadian Flag"
            className="w-16 h-10 sm:w-20 sm:h-12 lg:w-24 lg:h-14 object-cover rounded shadow-lg ring-1 ring-white/20"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/40 backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-sm text-primary font-medium" data-testid="hero-badge">{homepage.hero_badge}</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white font-['Outfit'] leading-tight drop-shadow-2xl" data-testid="hero-title">
                {(homepage.hero_title || '').split('|').map((part, i, arr) => (
                  <span key={i}>
                    <span className="text-gradient">{part.trim()}</span>
                    {i < arr.length - 1 && <br />}
                  </span>
                ))}
              </h1>
              
              <p className="text-lg text-white/90 max-w-xl leading-relaxed drop-shadow-md" data-testid="hero-mission">
                {homepage.mission_text}
              </p>
            </motion.div>

            {/* Prayer Times Card */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <LaserBorder>
                <div className="p-8 space-y-6" data-testid="jummah-card">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-secondary/10">
                      <Calendar className="h-5 w-5 text-secondary" />
                    </div>
                    <h3 className="text-xl font-semibold font-['Outfit'] text-foreground">Friday Jummah</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-accent/50">
                      <Clock className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">Khutbah</p>
                        <p className="font-semibold text-foreground prayer-display">1:30 PM</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-accent/50">
                      <Clock className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">Salat</p>
                        <p className="font-semibold text-foreground prayer-display">1:50 PM</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-accent/50">
                      <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Location</p>
                        <p className="font-medium text-foreground text-sm">54 St. Paul Crescent, Barrie, ON</p>
                      </div>
                    </div>
                  </div>

                  <Link to="/jummah" data-testid="jummah-details-btn">
                    <Button variant="ghost" className="w-full justify-between group">
                      View Details
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </LaserBorder>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Live Prayer Times Section */}
      <section className="py-8 bg-card/50 border-y border-border" data-testid="prayer-times-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <PrayerTimes />
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-24 lg:py-32" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Our Programs"
            title="Making a Difference Together"
            description="CRA-aligned charitable programs designed to support orphans and vulnerable families in our community."
          />

          <div className="bento-grid">
            {programs.map((program, index) => (
              <ProgramCard key={program.title} {...program} />
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/programs" data-testid="view-all-programs-btn">
              <Button size="lg" variant="outline" className="rounded-full px-8">
                View All Programs
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Main CTA Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-primary/10 via-background to-secondary/10 border-y border-border" data-testid="main-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
            <Link to="/donate" data-testid="main-donate-btn">
              <Button size="lg" className="w-full md:w-auto bg-primary text-primary-foreground hover:bg-primary/90 glow-teal rounded-full px-10 h-14 text-lg font-semibold">
                <Heart className="mr-2 h-5 w-5" />
                Donate Now
              </Button>
            </Link>
            <a 
              href="https://chat.whatsapp.com/BZpW0tw4MUuLAVuZmzBRDU?mode=gi_t"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="main-whatsapp-btn"
              className="w-full md:w-auto"
            >
              <Button size="lg" className="w-full md:w-auto rounded-full px-10 h-14 text-lg font-semibold bg-green-500 hover:bg-green-600 text-white">
                <MessageCircle className="mr-2 h-5 w-5" />
                Join WhatsApp
              </Button>
            </a>
            <Link to="/volunteer" data-testid="main-volunteer-btn" className="w-full md:w-auto">
              <Button size="lg" variant="outline" className="w-full md:w-auto rounded-full px-10 h-14 text-lg font-semibold border-2">
                <Users className="mr-2 h-5 w-5" />
                Volunteer
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section className="py-24 lg:py-32 bg-card islamic-pattern" data-testid="upcoming-events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Ramadan 1447 AH"
            title="Upcoming Events"
            description="Join us for Friday Jummah prayers and special Ramadan programs."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Next Jummah - Dynamic based on EST date */}
            {(() => {
              // Get current date in EST
              const now = new Date();
              const estOffset = -5 * 60;
              const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
              const estTime = new Date(utcTime + (estOffset * 60000));
              const todayEST = estTime.toISOString().split('T')[0];
              
              // Find next Friday from today
              const getNextFriday = () => {
                const today = new Date(todayEST + 'T12:00:00');
                const dayOfWeek = today.getDay();
                const daysUntilFriday = dayOfWeek <= 5 ? (5 - dayOfWeek) : (7 - dayOfWeek + 5);
                const nextFriday = new Date(today);
                nextFriday.setDate(today.getDate() + (daysUntilFriday === 0 && estTime.getHours() >= 14 ? 7 : daysUntilFriday));
                return nextFriday;
              };
              
              const nextFriday = getNextFriday();
              const fridayFormatted = nextFriday.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              });
              
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="p-6 rounded-2xl bg-background border border-primary/30"
                >
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-primary/20 text-primary border border-primary/30 mb-4">
                    JUMMAH
                  </span>
                  <h3 className="text-lg font-semibold font-['Outfit'] text-foreground mb-2">Weekly Jummah Prayer</h3>
                  <p className="text-sm text-muted-foreground mb-4">Join us for the Friday congregation.</p>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span>{fridayFormatted}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-primary" />
                      <span>Khutbah: 1:30 PM • Salat: 1:50 PM</span>
                    </div>
                  </div>
                </motion.div>
              );
            })()}

            {/* Next Taraweeh - Dynamic */}
            {(() => {
              const now = new Date();
              const estOffset = -5 * 60;
              const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
              const estTime = new Date(utcTime + (estOffset * 60000));
              const todayEST = estTime.toISOString().split('T')[0];
              
              // Taraweeh dates (excluding unavailable days)
              const taraweehDates = [
                { date: '2026-02-17', time: '7:30 PM' },
                { date: '2026-02-18', time: '7:30 PM' },
                { date: '2026-02-19', time: '7:30 PM' },
                { date: '2026-02-22', time: '7:30 PM' },
                { date: '2026-02-23', time: '7:30 PM' },
                { date: '2026-02-24', time: '7:30 PM' },
                { date: '2026-02-25', time: '7:30 PM' },
                { date: '2026-02-26', time: '7:30 PM' },
                { date: '2026-03-01', time: '7:30 PM' },
                { date: '2026-03-02', time: '7:45 PM' },
                { date: '2026-03-03', time: '7:45 PM' },
                { date: '2026-03-04', time: '7:45 PM' },
                { date: '2026-03-05', time: '7:45 PM' },
                { date: '2026-03-09', time: '8:00 PM' },
                { date: '2026-03-10', time: '8:00 PM' },
                { date: '2026-03-11', time: '8:00 PM' },
                { date: '2026-03-12', time: '8:00 PM' },
                { date: '2026-03-15', time: '8:00 PM' },
                { date: '2026-03-16', time: '8:00 PM' },
                { date: '2026-03-17', time: '8:00 PM' },
                { date: '2026-03-18', time: '8:00 PM' },
              ];
              
              const nextTaraweeh = taraweehDates.find(t => t.date >= todayEST);
              
              if (!nextTaraweeh) return null;
              
              const taraweehDate = new Date(nextTaraweeh.date + 'T12:00:00');
              const dateFormatted = taraweehDate.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              });
              
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="p-6 rounded-2xl bg-background border border-secondary/30"
                >
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-secondary/20 text-secondary border border-secondary/30 mb-4">
                    TARAWEEH
                  </span>
                  <h3 className="text-lg font-semibold font-['Outfit'] text-foreground mb-2">Isha & Taraweeh</h3>
                  <p className="text-sm text-muted-foreground mb-4">Join us for nightly Ramadan prayers.</p>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-secondary" />
                      <span>{dateFormatted}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-secondary" />
                      <span>{nextTaraweeh.time} EST</span>
                    </div>
                  </div>
                </motion.div>
              );
            })()}

            {/* Next Community Iftar - Dynamic */}
            {(() => {
              const now = new Date();
              const estOffset = -5 * 60;
              const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
              const estTime = new Date(utcTime + (estOffset * 60000));
              const todayEST = estTime.toISOString().split('T')[0];
              
              // Iftar dates
              const iftarDates = [
                { date: '2026-02-21', time: '5:56 PM' },
                { date: '2026-02-28', time: '6:06 PM' },
                { date: '2026-03-08', time: '6:16 PM' },
                { date: '2026-03-14', time: '6:24 PM' },
              ];
              
              const nextIftar = iftarDates.find(t => t.date >= todayEST);
              
              if (!nextIftar) return null;
              
              const iftarDate = new Date(nextIftar.date + 'T12:00:00');
              const dateFormatted = iftarDate.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              });
              
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="p-6 rounded-2xl bg-background border border-orange-500/30"
                >
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-orange-500/20 text-orange-400 border border-orange-500/30 mb-4">
                    IFTAR
                  </span>
                  <h3 className="text-lg font-semibold font-['Outfit'] text-foreground mb-2">Community Iftar</h3>
                  <p className="text-sm text-muted-foreground mb-4">Break your fast with the community.</p>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-orange-400" />
                      <span>{dateFormatted}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-orange-400" />
                      <span>{nextIftar.time} (Maghrib)</span>
                    </div>
                  </div>
                </motion.div>
              );
            })()}
          </div>

          <div className="text-center">
            <Link to="/events" data-testid="view-all-events-btn">
              <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8">
                View Full Schedule
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-24 lg:py-32 bg-card islamic-pattern" data-testid="impact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Our Impact"
            title="Together We Achieve More"
            description="Every contribution helps us serve more families in need."
          />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <ImpactCard value="500+" label="Families Served" icon={Users} />
            <ImpactCard value="100+" label="Orphans Supported" icon={Heart} />
            <ImpactCard value="52" label="Weekly Jummah Services" icon={BookOpen} />
            <ImpactCard value="1000+" label="Meals Distributed" icon={Utensils} />
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 lg:py-32" data-testid="newsletter-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary/20 via-background to-secondary/20 p-8 md:p-16 border border-border">
            <div className="absolute top-0 left-0 w-full h-full islamic-pattern opacity-50" />
            
            <div className="relative max-w-2xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-foreground">
                Stay Connected
              </h2>
              <p className="text-muted-foreground">
                Subscribe to our newsletter for updates on programs, events, and ways to get involved.
              </p>
              
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl bg-background/50"
                  data-testid="newsletter-email-input"
                  required
                />
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="h-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 px-8"
                  data-testid="newsletter-submit-btn"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Banner */}
      <section className="py-16 bg-green-500/10 border-y border-green-500/20" data-testid="whatsapp-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-full bg-green-500/20">
                <MessageCircle className="h-8 w-8 text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold font-['Outfit'] text-foreground">Join Our WhatsApp Community</h3>
                <p className="text-muted-foreground">Stay updated with announcements and connect with the community.</p>
              </div>
            </div>
            <a 
              href="https://chat.whatsapp.com/BZpW0tw4MUuLAVuZmzBRDU?mode=gi_t"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-banner-btn"
            >
              <Button size="lg" className="bg-green-500 hover:bg-green-600 text-white rounded-full px-8">
                <MessageCircle className="mr-2 h-5 w-5" />
                Join Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
